import Head from 'next/head'
import { useRouter } from 'next/router'

import metaImage from '@/assets/meta_image.jpg'
import { isColorPage, isTimespanPage } from '@/pages/commanders'
import { CardListType } from '@/types/card'
import { GenericPageProps } from '@/types/page'
import { TagLinkType } from '@/types/page'
import useDirectories from '@/utils/useDirectories'

export const writeDescription = (data: any, directories: ReturnType<typeof useDirectories>) => {
  const { dir, subpage, subsubpage } = directories

  const { card, cardlists } = data.container.json_dict

  // Find a relevant card list to pull cards from
  const cardList: CardListType | undefined = cardlists
    ? cardlists.find(({ tag }: CardListType) => tag === 'highliftcards') || cardlists[0]
    : undefined

  const tagLinks = data.panels?.taglinks as TagLinkType[]
  const cards = cardList?.cardviews.map(({ name }) => name).filter((name) => !name.includes(',')) // Commas lead to a less readable description
  const tags = tagLinks?.slice(0, 3).map(({ value }) => value)

  const descriptionLines: string[] = []

  switch (dir) {
    case 'cards':
      if (subpage) {
        let descriptionLine = `Top commanders and EDH deck recommendations based on ${card.num_decks.toLocaleString()} ${card.name} decks.`
        if (cards) descriptionLine += ` Played with ${cards!.slice(0, 3).join(', ')}, and more.`
        descriptionLines.push(descriptionLine)
      }
      break
    case 'commanders':
      if (subpage === undefined) {
        // /commanders/
      } else if (isColorPage(subpage)) {
        // /commanders/{color}
      } else if (isTimespanPage(subpage)) {
        // /commanders/{timespan}
      } else {
        if (!subsubpage) {
          if (card)
            descriptionLines.push(`EDH deck recommendations from ${card.num_decks.toLocaleString()} Commander decks.`)
          if (tags) descriptionLines.push(`${tags!.slice(0, 2).join(', ')}, and ${tags![2]}.`)
          if (cards) descriptionLines.push(`${cards!.slice(0, 3).join(', ')}, and more.`)
        }
      }
      break
  }

  // If no description, fallback to Python generated description
  if (descriptionLines.length === 0) return data.container.description

  const description = descriptionLines.join('\n')
  console.log(description)
  return description
}

export type ExplicitHeadProps = {
  description?: string
  imageUrl?: string
  keywords?: string
  noindex?: boolean
  title: string
}

export const ExplicitHead = ({ description, imageUrl, keywords, noindex, title }: ExplicitHeadProps) => {
  const router = useRouter()

  title = title.includes('EDHREC') ? title : `${title} | EDHREC`
  const image = imageUrl ? imageUrl : metaImage.src
  const url = `https://edhrec.com${router.asPath === '/' ? '' : router.asPath}`

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta content={title} name='title' />
        <meta content={title} property='og:title' />
        <meta content={description} name='description' />
        <meta content={description} property='og:description' />
        <meta content={keywords} name='keywords' />
        <meta content={keywords} property='og:keywords' />
        {noindex ? (
          <meta content='noindex' name='robots' />
        ) : (
          <>
            <link href={url} rel='canonical' />
            <meta content={url} property='og:url' />
          </>
        )}
        <meta content='website' property='og:type' />
        <meta content='EDHREC' property='og:site_name' />
        <meta content={image} property='og:image' />
        <meta content='summary_small_image' name='twitter:card' />
        <meta content='EDHREC' name='twitter:image:alt' />

        {/* preconnect to resources we're very likely going to need */}
        {/* no more than 6. look at "Connection View" in WebPageTest */}
        <link href='https://json.edhrec.com' rel='preconnect' />
        <link href='https://cloudflare.edhrec.com' rel='preconnect' />
        <link href='https://cards.scryfall.io' rel='preconnect' />
        <link href='https://scripts.mediavine.com' rel='preconnect' />
        <link href='https://www.googletagmanager.com' rel='preconnect' />
        <link href='https://www.youtube.com' rel='preconnect' />

        {/* dns-prefetch the same things as a fallback */}
        {/* "Implementing dns-prefetch fallback in the same <link> tag causes a bug in Safari where preconnect gets cancelled." */}
        <link href='https://json.edhrec.com' rel='dns-prefetch' />
        <link href='https://cloudflare.edhrec.com' rel='dns-prefetch' />
        <link href='https://cards.scryfall.io' rel='dns-prefetch' />
        <link href='https://scripts.mediavine.com' rel='dns-prefetch' />
        <link href='https://www.googletagmanager.com' rel='dns-prefetch' />
        <link href='https://www.youtube.com' rel='dns-prefetch' />

        {/* dns-prefetch less critical stuff we might need */}
        <link href='https://keywords.mediavine.com' rel='dns-prefetch' />
        <link href='https://exchange.mediavine.com' rel='dns-prefetch' />
        <link href='https://secure.gravatar.com' rel='dns-prefetch' />
        <link href='https://www.google-analytics.com' rel='dns-prefetch' />
      </Head>
    </>
  )
}

type Props = {
  data: GenericPageProps['data']
}

const DefaultHead = ({ data }: Props) => {
  const directories = useDirectories()
  return (
    <ExplicitHead
      description={writeDescription(data, directories)}
      imageUrl={data.container?.json_dict?.card?.image_uris[0]?.art_crop}
      keywords={data.container.keywords}
      title={data.container.title}
    />
  )
}

export default DefaultHead
